import React, { useContext, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Tabs, Tab, Button, Stack, Chip, Alert,
  CircularProgress, Divider,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ArticleIcon from '@mui/icons-material/Article';
import { useQuery } from '@tanstack/react-query';
import axiosInstance from '../../../axios/axiosinstance';
import { AuthContext } from '../../../context/logeduser';
import { form16PdfUrl } from '../../../hooks/useForm16';
import Form16PartA from '../../payroll/Form16/Form16PartA';
import Form16PartB from '../../payroll/Form16/Form16PartB';
import PageHeader from '../PageHeader';

// ---- Visual tokens (shared with the employee Home, Attendance & Payslip dashboards) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ACCENT = '#2f6bff';

const btnSx = { textTransform: 'none', fontWeight: 700, borderRadius: '12px' };

export default function Form16EmpDetail() {
  const { id } = useParams();
  const { authData } = useContext(AuthContext);
  const employeeNo = authData?.employee_no;
  const [tab, setTab] = useState(0);
  const { data, isLoading, error } = useQuery({
    queryKey: ['my-form16-detail', employeeNo, id],
    queryFn: () =>
      axiosInstance.get(`api/form16/employee/${employeeNo}/${id}/`).then((r) => r.data),
    enabled: !!employeeNo && !!id,
  });

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  if (error || !data) return <Box sx={{ pb: 2 }}><Alert severity="error" sx={{ borderRadius: '14px' }}>Form 16 not found.</Alert></Box>;

  const published = data.status === 'PUBLISHED';

  return (
    <Box sx={{ pb: { xs: 4, md: 2 } }}>
      {/* ---- Header card (shared PageHeader) ---- */}
      <PageHeader
        icon={<ArticleIcon />}
        title={`Form 16 — FY ${data.fy_label}`}
        subtitle={(
          <>
            Period {data.period_from} → {data.period_to}
            {data.certificate_no ? ` · ${data.certificate_no}` : ''}
          </>
        )}
        action={(
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              label={data.status}
              sx={{ fontWeight: 700, bgcolor: published ? '#f0fdf4' : '#f3f4f6', color: published ? '#16a34a' : '#6b7280' }}
            />
            <Button component={RouterLink} to="/form16" size="small" variant="outlined" sx={{ ...btnSx, color: '#6b7280', borderColor: '#e5e7eb' }}>Back</Button>
          </Stack>
        )}
      />

      {/* ---- Download actions ---- */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 }, p: { xs: 1.5, sm: 2 } }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button startIcon={<DownloadIcon />} component="a" href={form16PdfUrl(data.id, 'part-a')} target="_blank" sx={{ ...btnSx, color: ACCENT }}>
            Part A PDF
          </Button>
          <Button startIcon={<DownloadIcon />} component="a" href={form16PdfUrl(data.id, 'part-b')} target="_blank" sx={{ ...btnSx, color: ACCENT }}>
            Part B PDF
          </Button>
          <Button
            startIcon={<DownloadIcon />}
            variant="contained"
            disableElevation
            component="a"
            href={form16PdfUrl(data.id, 'combined')}
            target="_blank"
            sx={{ ...btnSx, background: 'linear-gradient(135deg,#3b82f6,#2f6bff)', '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' } }}
          >
            Combined PDF
          </Button>
          <Button component={RouterLink} to="/form16/rectify" sx={{ ...btnSx, color: '#6b7280' }}>Raise rectification</Button>
        </Stack>
      </Paper>

      {/* ---- Part A / Part B tabs ---- */}
      <Paper elevation={0} sx={{ ...CARD_SX, p: 0, overflow: 'hidden' }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{
            px: 1,
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, color: '#6b7280' },
            '& .Mui-selected': { color: `${ACCENT} !important` },
            '& .MuiTabs-indicator': { backgroundColor: ACCENT, height: 3, borderRadius: 3 },
          }}
        >
          <Tab label="Part A — TDS Summary" />
          <Tab label="Part B — Salary & Tax Computation" />
        </Tabs>
        <Divider sx={{ borderColor: '#eef0f4' }} />
        <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          {tab === 0 && <Form16PartA form16={data} readOnly />}
          {tab === 1 && <Form16PartB form16={data} />}
        </Box>
      </Paper>
    </Box>
  );
}
